"use strict";
/* globals module */
/**
 * @module meteringClient
 * @description  Metering Client provides an easy way to consume  Metering REST API end-points. In order to obtain a needed routes `meteringClient` uses `meteringRoute`.
 */
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var inversify_1 = require("inversify");
;
var httpApi_1 = require("../../httpApi");
var _1 = require("./");
var MeteringClient = /** @class */ (function () {
    function MeteringClient(meteringRoute, apiClient, meteringBatchClient, meteringStatisticsClient, meteringACLClient, meteringCategoryClient, meteringSettingsClient) {
        this.meteringRoute = meteringRoute;
        this.apiClient = apiClient;
        this.meteringBatchClient = meteringBatchClient;
        this.meteringStatisticsClient = meteringStatisticsClient;
        this.meteringACLClient = meteringACLClient;
        this.meteringCategoryClient = meteringCategoryClient;
        this.meteringSettingsClient = meteringSettingsClient;
    }
    Object.defineProperty(MeteringClient.prototype, "routeDefinition", {
        get: function () {
            return this.meteringRoute;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MeteringClient.prototype, "batch", {
        get: function () {
            return this.meteringBatchClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MeteringClient.prototype, "statistics", {
        get: function () {
            return this.meteringStatisticsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MeteringClient.prototype, "acl", {
        get: function () {
            return this.meteringACLClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MeteringClient.prototype, "settings", {
        get: function () {
            return this.meteringSettingsClient;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(MeteringClient.prototype, "category", {
        get: function () {
            return this.meteringCategoryClient;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns a promise that is resolved once the find action has been performed. Success response returns a list of metering resources matching the given criteria.
     * @method
     * @param options Query resource options object.
     * @returns A promise that is resolved once the find action has been performed.
     * @example meteringClient.find({
                    pageNumber : 1,
                    pageSize : 10,
                    orderBy : '<field>',
                    orderDirection : '<asc|desc>',
                    categories: 'Storage,Requests,Bandwidth'
                })
                .then(function (collection) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
     **/
    MeteringClient.prototype.find = function (options) {
        return this.apiClient.get(this.routeDefinition.find(options));
    };
    /**
     * Returns a promise that is resolved once the get action has been performed. Success response returns the metering resource.
     * @method
     * @param id MeteringData id which uniquely identifies MeteringData resource that needs to be retrieved.
     * @param options Query resource options object.
     * @returns A promise that is resolved once the get action has been performed.
     * @example meteringClient.get()
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    MeteringClient.prototype.get = function (id, options) {
        return this.apiClient.get(this.meteringRoute.get(id, options));
    };
    /**
     * Returns a promise that is resolved once the create metering action has been performed; this action creates a new metering resource.
     * @method
     * @param data An MeteringData object that needs to be inserted into the system.
     * @returns A promise that is resolved once the create metering action has been performed
     * @example meteringClient.create({
                    category : '<category-name>',
                    name : '<sub-category-name>',
                    value: '<value>'
                })
                .then(function (data) {
                    // perform success action here
                },
                 function (response, status, headers, config) {
                     // perform error handling here
                });
      **/
    MeteringClient.prototype.create = function (data) {
        return this.apiClient.post(this.routeDefinition.create(), this.routeDefinition.createParams(data));
    };
    /**
     * Returns a promise that is resolved once the update metering action has been performed; this action updates a metering resource. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `meteringRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.updateParams(meteringData);
     * let uri = params['model'].links('put').href;
     * ```
     * @method
     * @param data An metering data object used to update specified MeteringData resource.
     * @returns A promise that is resolved once the update metering action has been performed.
     * @example // meteringData is a resource previously fetched using get action.
                    meteringData.value = '<some-new-value>';
                    meteringClient.update(meteringData)
                        .then(function (data) {
                            // perform success action here
                        },
                         function (response, status, headers, config) {
                             // perform error handling here
                        });
     **/
    MeteringClient.prototype.update = function (data) {
        return this.apiClient.put(this.routeDefinition.update(data), this.routeDefinition.updateParams(data));
    };
    /**
     * Returns a promise that is resolved once the remove action has been performed. This action will remove a metering resource from the system if successfully completed. This route uses HAL enabled objects to obtain routes and therefore it doesn't apply `meteringRoute` route template. Here is an example of how a route can be obtained from HAL enabled objects:
     * ```
     * let params = modelMapper.removeParams(meteringData);
     * let uri = params['model'].links('delete').href;
     * ```
     * @method
     * @param data An metering data object used to remove specified MeteringData resource.
     * @returns A promise that is resolved once the remove action has been performed.
     * @example // meteringData is a resource previously fetched using get action.
                        meteringClient.remove(meteringData)
                            .then(function (data) {
                                // perform success action here
                            },
                             function (response, status, headers, config) {
                                 // perform error handling here
                            });
     **/
    MeteringClient.prototype.remove = function (data) {
        return this.apiClient.delete(this.routeDefinition.delete(data));
    };
    /**
     * Returns a promise that is resolved once the purge action has been performed. This action will remove all metering resources from the system if successfully completed.
     * @method
     * @returns A promise that is resolved once the purge action has been performed.
     * @example meteringClient.purge()
                    .then(function (data) {
                        // perform success action here
                    },
                     function (response, status, headers, config) {
                         // perform error handling here
                    });
     **/
    MeteringClient.prototype.purge = function () {
        return this.apiClient.delete(this.routeDefinition.purge());
    };
    var _a, _b, _c, _d, _e, _f, _g;
    MeteringClient = tslib_1.__decorate([
        inversify_1.injectable(),
        tslib_1.__param(0, inversify_1.inject(_1.TYPES.MeteringRoute)),
        tslib_1.__param(1, inversify_1.inject(httpApi_1.httpTYPES.ApiClient)),
        tslib_1.__param(2, inversify_1.inject(_1.TYPES.MeteringBatchClient)),
        tslib_1.__param(3, inversify_1.inject(_1.TYPES.MeteringStatisticsClient)),
        tslib_1.__param(4, inversify_1.inject(_1.TYPES.MeteringACLClient)),
        tslib_1.__param(5, inversify_1.inject(_1.TYPES.MeteringCategoryClient)),
        tslib_1.__param(6, inversify_1.inject(_1.TYPES.MeteringSettingsClient)),
        tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof _1.MeteringRoute !== "undefined" && _1.MeteringRoute) === "function" ? _a : Object, typeof (_b = typeof httpApi_1.ApiClient !== "undefined" && httpApi_1.ApiClient) === "function" ? _b : Object, typeof (_c = typeof _1.MeteringBatchClient !== "undefined" && _1.MeteringBatchClient) === "function" ? _c : Object, typeof (_d = typeof _1.MeteringStatisticsClient !== "undefined" && _1.MeteringStatisticsClient) === "function" ? _d : Object, typeof (_e = typeof _1.MeteringACLClient !== "undefined" && _1.MeteringACLClient) === "function" ? _e : Object, typeof (_f = typeof _1.MeteringCategoryClient !== "undefined" && _1.MeteringCategoryClient) === "function" ? _f : Object, typeof (_g = typeof _1.MeteringSettingsClient !== "undefined" && _1.MeteringSettingsClient) === "function" ? _g : Object])
    ], MeteringClient);
    return MeteringClient;
}());
exports.MeteringClient = MeteringClient;
/**
 * @copyright (c) 2017 Mono Ltd
 * @license MIT
 * @author Mono Ltd
 * @overview
 ***Notes:**
 - Refer to the [Baasic REST API](http://dev.baasic.com/api/reference/home) for detailed information about available Baasic REST API end-points.
 - All end-point objects are transformed by the associated route service.
 */ 
